// Help text for the merge-readiness indicators on the pull request pages.
//
// The distinction that trips people up: `canMerge` is derived from GitHub's `mergeable` flag,
// which only reports whether the two branches combine without conflicts. Branch-protection
// blockers — required reviews, required status checks, out-of-date branches — never show up
// there; they only appear in `mergeable_state`. So a PR can read "Can merge" while GitHub
// itself still refuses to merge it, and the two indicators have to be read together.

export const CAN_MERGE_HELP =
  'Git can combine this branch with its base without conflicts, and the PR is open and not a draft. ' +
  'This does not mean GitHub will accept the merge — approvals and required status checks are ' +
  'reported by the merge state next to this.';

export const CANNOT_MERGE_HELP =
  'GitHub will not merge this as it stands: it either conflicts with the base branch, is closed, ' +
  'or is still a draft. The merge state next to this says which.';

// GitHub's `mergeable_state` is absent from the REST reference but the value set is stable.
export const MERGE_STATE_HELP: Record<string, string> = {
  clean: 'No conflicts, and every required review and status check has passed. GitHub will merge this.',
  unstable:
    'No conflicts and nothing required is failing, but a status check that branch protection does ' +
    'not require is failing or still running. GitHub will still let you merge.',
  blocked:
    'Branch protection is holding the merge: a required review or a required status check has not passed.',
  dirty: 'The branch conflicts with its base branch and has to be updated before it can merge.',
  behind:
    'The branch is behind its base and protection requires it to be up to date. Update the branch to merge.',
  draft: 'This is a draft pull request. Mark it ready for review before it can merge.',
  has_hooks: 'Mergeable and passing, but a pre-receive hook runs when the merge happens.',
  unknown:
    'GitHub has not finished working out whether this merges. It normally settles within a few seconds.',
};

export function mergeStateHelp(state: string): string {
  return MERGE_STATE_HELP[state] ?? 'GitHub reported a merge state of "' + state + '" that deliver has no description for.';
}
